import { compressToURL } from "./compress";
import { getJourneyDurationInMinutes } from "./durationUtils";
import { decodeHafasReference, encodeHafasReference, HafasLeg, HafasReference, stripRedundantAttributes } from "./hafas-reference";

export type Leg = {
  id: string,
  line: any,
  origin: any,
  destination: any,
  walking: false,
  cancelled: boolean,
  loadFactor?: string,
  platformTransfer?: PlatformTransfer
  transferStats?: TransferStat
  stopovers: any[],
  direction: any,
  remarks: any[],

  arrival?: string,
  plannedArrival: string,
  plannedArrivalPlatform?: string,
  arrivalPlatform?: string,
  arrivalDelay?: number,
  arrDelay?: any,  // historic data

  departure?: string,
  plannedDeparture: string,
  plannedDeparturePlatform?: string,
  departurePlatform?: string,
  departureDelay?: number,
  depDelay?: any, // historic data
};

export type TransferStat = {
  // null when no caught connections can be averaged; an absent TransferStat means no data.
  avgSecondsToChange: number | null;
  percentageMissedConnections: number;
  percentageCancelledConnections: number;
};

/** Matches API `PlatformTransferInfo` on each leg after journey processing. */
export type PlatformTransfer = {
  sameStation: boolean;
  sameTrack: boolean;
  samePlatform: boolean | null;
  arrivalPlatform: string | null;
  departurePlatform: string | null;
  arrivalStationId: string;
  reason?: string;
};

export type TransferPlatformIndicator = 'same-track' | 'same-platform' | 'different-station';

/**
 * Same priority as iOS TransferView.transferIndicatorType.
 * When hideForCriticalState (cancelled / infeasible / missed / likely miss), show no platform badge.
 */
export function getTransferPlatformIndicator(
  platformTransfer: PlatformTransfer | null | undefined,
  hideForCriticalState: boolean
): TransferPlatformIndicator | null {
  if (hideForCriticalState) return null;
  if (!platformTransfer) return null;
  if (!platformTransfer.sameStation) return 'different-station';
  if (platformTransfer.sameTrack) return 'same-track';
  if (platformTransfer.samePlatform === true) return 'same-platform';
  return null;
}

export type ReliabilityScoreCompleteness = 'full' | 'partial' | 'null';

export type ReliabilityScoreResult = {
  reliabilityScore: number | null;
  reliabilityScoreCompleteness: ReliabilityScoreCompleteness;
}

export type Journey = {
  tags: string[];
  price?: Price;  // Make price optional
  reliability: ReliabilityScoreResult;
  legs: Leg[];
  transferStats: (TransferStat | null)[]
  journeyDelayStats: {
    averageDelayAtDest?: number | null;
    journeyCancellationRate?: number | null;
  },
  refreshToken: string;
}

type Tag = 'arrives-first' | 'cheapest' | 'fastest' | 'most-reliable' | 'direct' | 'least-transfers' | 'departs-first' | 'least-busy'

type Price = {
  amount: number;
  currency: string;
  hint?: any;
}

export type JourneyWithHafas = Journey & {
  id: string; // compressed HAFAS reference token
  hafas: HafasReference;  // parsed HAFAS reference token (from refreshToken)
}

export type TaggedJourney = JourneyWithHafas & {
  tagsData: DataForTags;
  tags?: string[];
}

type DataForTags = {
  price: number | null;  // Allow null prices
  arrivalTime: string;
  duration: number;
  reliabilityScore: number | null;  // Allow null reliability scores
  direct: boolean;
  numberOfTransfers: number;
  loadFactorScore: number | null;  // Allow null load factor scores
}

// Helper to convert loadFactor string to numeric score
const loadFactorToScore = (loadFactor: string | undefined): number | null => {
  if (!loadFactor) return null;
  const mapping: Record<string, number> = {
    'low': 1,
    'low-to-medium': 2,
    'medium': 3,
    'medium-to-high': 4,
    'high': 5,
    'very-high': 6,
    'exceptionally-high': 7
  };
  return mapping[loadFactor] ?? null;
};

const generateTagsData = (journey: Journey): DataForTags => {
  const getArrivalTime = (journey: Journey) => {
    const lastLeg = journey.legs[journey.legs.length - 1];
    if (!!lastLeg.arrival) return lastLeg.arrival;
    return lastLeg.plannedArrival;
  } 
  
  // Calculate duration using only planned times for 'Fastest' tag
  const plannedDuration = () => {
    const plannedDeparture = journey.legs[0]?.plannedDeparture;
    const plannedArrival = journey.legs[journey.legs.length - 1]?.plannedArrival;
    if (!plannedDeparture || !plannedArrival) return getJourneyDurationInMinutes(journey.legs);
    return Math.round((new Date(plannedArrival).getTime() - new Date(plannedDeparture).getTime()) / 60000);
  }
  
  // Calculate loadFactorScore from legs (average or max)
  const calculateLoadFactorScore = (): number | null => {
    const scores = journey.legs
      .map(leg => loadFactorToScore(leg.loadFactor))
      .filter((score): score is number => score !== null);
    
    if (scores.length === 0) return null;
    // Use average load factor score
    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
  }

  return {
    price: journey.price?.amount ?? null,
    reliabilityScore: journey.reliability?.reliabilityScore ?? null,
    arrivalTime: getArrivalTime(journey),
    duration: plannedDuration(),
    direct: journey.legs.length === 1,
    numberOfTransfers: journey.legs.length - 1,
    loadFactorScore: calculateLoadFactorScore()
  }
}

const generateTags = (
  tagsData: DataForTags, 
  context: JourneyContext,
  journey: Journey,
  allJourneys: Journey[]
): Tag[] => {
  const journeyDepartureTime = getJourneyDepartureTime(journey);
  const journeyArrivalTime = getJourneyArrivalTime(journey);
  
  // Count journeys with each property to determine if only one has it
  const journeysWithPrice = allJourneys.filter(j => generateTagsData(j).price !== null).length;
  const journeysWithReliability = allJourneys.filter(j => generateTagsData(j).reliabilityScore !== null).length;
  const journeysWithLoadFactor = allJourneys.filter(j => generateTagsData(j).loadFactorScore !== null).length;
  
  const tags: (Tag | undefined)[] = [];
  
  // Direct tag - only show if not all journeys are direct
  const allDirect = allJourneys.every(j => j.legs.length === 1);
  if (tagsData.direct && !allDirect) {
    tags.push('direct');
  }
  
  // Cheapest tag - only show if multiple journeys have prices and not all same price
  if (tagsData.price !== null && context.cheapest === tagsData.price && journeysWithPrice > 1) {
    const prices = allJourneys.map(j => generateTagsData(j).price).filter((p): p is number => p !== null);
    const allSamePrice = new Set(prices).size === 1;
    if (!allSamePrice) {
      tags.push('cheapest');
    }
  }
  
  // Most Reliable tag - only show if multiple journeys have reliability scores and not all same
  if (tagsData.reliabilityScore !== null && context.mostReliable === tagsData.reliabilityScore && journeysWithReliability > 1) {
    const reliabilities = allJourneys.map(j => generateTagsData(j).reliabilityScore).filter((r): r is number => r !== null);
    const allSameReliability = new Set(reliabilities).size === 1;
    if (!allSameReliability) {
      tags.push('most-reliable');
    }
  }
  
  // Fastest tag - only show if not all journeys have same duration
  if (context.fastest === tagsData.duration) {
    const allSameDuration = allJourneys.every(j => generateTagsData(j).duration === tagsData.duration);
    if (!allSameDuration) {
      tags.push('fastest');
    }
  }
  
  // Arrives First / Departs First tags
  let suppressArrivesFirst = false;
  let suppressDepartsFirst = false;
  if (allJourneys.length > 0) {
    const first = allJourneys[0];
    if (context.arrivesFirst === getJourneyArrivalTime(first)) {
      suppressArrivesFirst = true;
    }
    if (context.departsFirst === getJourneyDepartureTime(first)) {
      suppressDepartsFirst = true;
    }
  }

  let showArrivesFirst = false;
  let showDepartsFirst = false;
  
  if (context.arrivesFirst === journeyArrivalTime && !suppressArrivesFirst) {
    showArrivesFirst = true;
  }
  if (context.departsFirst === journeyDepartureTime && !suppressDepartsFirst) {
    showDepartsFirst = true;
  }
  // If both would be shown, only show Arrives First
  if (showArrivesFirst) {
    tags.push('arrives-first');
  } else if (showDepartsFirst) {
    tags.push('departs-first');
  }
  
  // Least Transfers tag - only show if not all journeys have same transfers
  if (context.showLeastTransfers && tagsData.numberOfTransfers === context.leastTransfers) {
    const allSameTransfers = allJourneys.every(j => j.legs.length - 1 === tagsData.numberOfTransfers);
    if (!allSameTransfers) {
      tags.push('least-transfers');
    }
  }
  
  // Least Busy tag - only show if multiple journeys have load factor scores, score matches, score < 5, and not all same
  if (context.showLeastBusy && 
      tagsData.loadFactorScore !== null && 
      context.leastBusyScore !== null &&
      tagsData.loadFactorScore === context.leastBusyScore && 
      tagsData.loadFactorScore < 5 && 
      journeysWithLoadFactor > 1) {
    const loadFactors = allJourneys.map(j => generateTagsData(j).loadFactorScore).filter((lf): lf is number => lf !== null);
    const allSameLoadFactor = new Set(loadFactors).size === 1;
    if (!allSameLoadFactor) {
      tags.push('least-busy');
    }
  }

  return tags.filter((tag): tag is Tag => tag !== undefined);
}

const getJourneyDepartureTime = (journey: Journey): string => {
  const firstLeg = journey.legs[0];
  return firstLeg.departure || firstLeg.plannedDeparture;
};

const getJourneyArrivalTime = (journey: Journey): string => {
  const lastLeg = journey.legs[journey.legs.length - 1];
  return lastLeg.arrival || lastLeg.plannedArrival;
};

type JourneyContext = {
  cheapest: number | null;  // Allow null for when no valid prices exist
  mostReliable: number | null;  // Allow null for when no valid reliability scores exist
  fastest: number;
  arrivesFirst: string;
  departsFirst: string;
  leastTransfers: number;
  showLeastTransfers: boolean;
  leastBusyScore: number | null;
  showLeastBusy: boolean;
}

const calculateJourneyContext = (journeys: Journey[]): JourneyContext => {
  if (journeys.length === 0) {
    return {
      cheapest: null,
      mostReliable: null,
      fastest: Number.MAX_VALUE,
      arrivesFirst: '',
      departsFirst: '',
      leastTransfers: Number.MAX_VALUE,
      showLeastTransfers: false,
      leastBusyScore: null,
      showLeastBusy: false
    };
  }

  let cheapest: number | null = null;
  let mostReliable: number | null = null;
  let fastest = Number.MAX_VALUE;
  let arrivesFirst = getJourneyArrivalTime(journeys[0]);
  let departsFirst = getJourneyDepartureTime(journeys[0]);
  let leastTransfers = Number.MAX_VALUE;
  let leastBusyScore: number | null = null;

  const hasDirect = journeys.some(journey => journey.legs.length === 1);

  for (let journey of journeys) {
    const tagsData = generateTagsData(journey);

    // Only update cheapest if we have a valid price
    if (tagsData.price !== null && (cheapest === null || tagsData.price < cheapest)) {
      cheapest = tagsData.price;
    }
    // Only update mostReliable if we have a valid reliability score
    if (tagsData.reliabilityScore !== null && (mostReliable === null || tagsData.reliabilityScore > mostReliable)) {
      mostReliable = tagsData.reliabilityScore;
    }
    if (tagsData.duration < fastest) {
      fastest = tagsData.duration;
    }
    
    const journeyArrivalTime = getJourneyArrivalTime(journey);
    const journeyDepartureTime = getJourneyDepartureTime(journey);

    if (new Date(arrivesFirst) > new Date(journeyArrivalTime)) {
      arrivesFirst = journeyArrivalTime;
    }
    if (new Date(departsFirst) > new Date(journeyDepartureTime)) {
      departsFirst = journeyDepartureTime;
    }

    if (hasDirect === false && tagsData.numberOfTransfers < leastTransfers) {
      leastTransfers = tagsData.numberOfTransfers;
    }
    
    // Least busy
    if (tagsData.loadFactorScore !== null && (leastBusyScore === null || tagsData.loadFactorScore < leastBusyScore)) {
      leastBusyScore = tagsData.loadFactorScore;
    }
  }

  const showLeastTransfers = journeys.filter(j => 
    generateTagsData(j).numberOfTransfers === leastTransfers
  ).length !== journeys.length;
  
  // Show least busy only if not all journeys have the same score and at least one journey has a score
  const leastBusyJourneys = journeys.map(j => generateTagsData(j).loadFactorScore).filter((score): score is number => score !== null);
  const showLeastBusy = leastBusyJourneys.length > 0 && leastBusyJourneys.some(score => score !== (leastBusyScore ?? -1));

  return {
    cheapest,
    mostReliable,
    fastest,
    arrivesFirst,
    departsFirst,
    leastTransfers,
    showLeastTransfers,
    leastBusyScore,
    showLeastBusy
  };
}

const sortJourneysByDeparture = (journeys: Journey[]): Journey[] => {
  return [...journeys].sort((a, b) => {
    const timeA = new Date(getJourneyDepartureTime(a));
    const timeB = new Date(getJourneyDepartureTime(b));
    return timeA.getTime() - timeB.getTime();
  });
};

export const addHafasAttributes = (journey: Journey): JourneyWithHafas => {
  // set ID to compressed HAFAS reference token
  const hafas = decodeHafasReference(journey.refreshToken);
  const minimalHafas = stripRedundantAttributes(hafas);
  const id = compressToURL(encodeHafasReference(minimalHafas));
  let prevHafasLegIndex = 0;

  return {
    ...journey,
    id,
    hafas,
  }
}

export const mapJourneysData = (journeys: Journey[], existingJourneys: Journey[] = []): TaggedJourney[] => {
  // Combine existing and new journeys
  const allJourneys = sortJourneysByDeparture([...existingJourneys, ...journeys]);

  // Calculate context for all journeys
  const journeyContext = calculateJourneyContext(allJourneys);

  // Map and tag journeys, deduplicating by ID
  const seenIds = new Set<string>();
  const mappedJourneys = allJourneys.reduce((acc: TaggedJourney[], _journey) => {
    const journey = addHafasAttributes(_journey);

    if (seenIds.has(journey.id)) return acc;
    seenIds.add(journey.id);

    return [
      ...acc,
      {
        ...journey,
        tagsData: generateTagsData(journey),
        tags: generateTags(generateTagsData(journey), journeyContext, journey, allJourneys)
      }
    ];
  }, []);

  return mappedJourneys;
}

/** Format FlixTrain adminCode (e.g. FLX30_) for display as "FLX 30". */
function formatFlxAdminCode(adminCode: string): string {
  const cleaned = adminCode.replace(/_/g, '');
  const match = cleaned.match(/^([A-Z]+)(\d+)$/);
  return match ? `${match[1]} ${match[2]}` : adminCode;
}

export function getLineName(leg: any) {
  const baseName = leg.depDelay?.originalRoute ?? leg.arrDelay?.originalRoute ?? leg.line?.deutscheBahnLineName ?? leg.line?.name;
  // For FLX: use adminCode, e.g. FLX30_ -> "FLX 30", instead of trip number
  if (baseName?.startsWith('FLX') && leg.line?.adminCode) {
    return formatFlxAdminCode(leg.line.adminCode);
  }
  return baseName;
}
